import { createStore } from 'react-sweet-state';
import { orderBy, uniqueId } from 'lodash';

import { AUDIENCE_TARGET_TYPE, withoutDefaultAudienceTarget } from 'src/data/audiencesTargeting';
import {
  createAudienceTargeting,
  deleteAudienceTargeting,
  queryAudienceSegments,
} from 'src/graphql/continuousOptimization';

import { CHANGE_TYPE, getChangesList, getIdsToChange } from './utils';

const NAME = 'page-project-audience-segment';

const INITIAL_STATE = {
  targets: [],
  targetChangesObj: {},
  processing: false,

  segments: [],
  loadingSegments: false,
  errorSegments: null,
};

const sortTargets = (list) =>
  orderBy(list, ['audienceTarget.orderIndex', 'audienceTarget.id', 'id'], 'asc');

const PRIVATE_ACTIONS = {
  addCreateChange:
    (id) =>
    ({ getState, setState }) => {
      const { targetChangesObj } = getState();
      const newValue = { ...targetChangesObj, [id]: CHANGE_TYPE.create };
      setState({ targetChangesObj: newValue });
    },

  addDeleteChange:
    (id) =>
    ({ getState, setState }) => {
      const { targetChangesObj } = getState();
      if (targetChangesObj[id] !== CHANGE_TYPE.create) {
        const newValue = { ...targetChangesObj, [id]: CHANGE_TYPE.delete };
        setState({ targetChangesObj: newValue });
      }
    },

  removeChange:
    (id) =>
    ({ getState, setState }) => {
      const { targetChangesObj } = getState();
      const newValue = { ...targetChangesObj, [id]: null };
      setState({ targetChangesObj: newValue });
    },

  mergeSegmentInCache:
    (segment) =>
    ({ getState, setState }) => {
      const { segments: list } = getState();
      const newList = list.filter(({ id }) => id !== segment.id);
      setState({ segments: [...newList, segment] });
    },

  checkUniqueTargets:
    (target) =>
    ({ getState }) => {
      const isSameTarget = (item) =>
        item.targetType === target.targetType &&
        item.targetEvaluation === target.targetEvaluation &&
        item.value === target.value;

      const { targets } = getState();
      const withoutCurrent = targets.filter(({ id }) => id !== target.id);
      const hasSameData = withoutCurrent.some(isSameTarget);
      if (hasSameData) {
        throw Error('Same audience target already exists for this campaign');
      }
    },
};

const ACTIONS = {
  getSegment:
    (segmentId) =>
    ({ getState }) => {
      const { segments } = getState();
      return segments.find(({ id }) => id === segmentId);
    },

  querySegments:
    (projectId) =>
    async ({ setState }) => {
      try {
        setState({
          loadingSegments: true,
          errorSegments: null,
        });

        const segments = await queryAudienceSegments(projectId);
        setState({ segments });
        return segments;
      } catch (error) {
        setState({ errorSegments: error.message });
        return [];
      } finally {
        setState({ loadingSegments: false });
      }
    },

  setSegmentStatus:
    (segmentId, status) =>
    ({ dispatch }) => {
      const segment = dispatch(ACTIONS.getSegment(segmentId));
      if (segment) {
        dispatch(PRIVATE_ACTIONS.mergeSegmentInCache({ ...segment, status }));
      }
    },

  getTargets:
    () =>
    ({ getState }) => {
      return getState().targets;
    },

  updateSegmentKeywords:
    (segmentId, keywords) =>
    ({ getState, setState, dispatch }) => {
      const segment = dispatch(ACTIONS.getSegment(segmentId));
      segment.keywords = keywords;
      const { segments: list } = getState();
      const newList = list.filter(({ id }) => id !== segment.id);
      setState({ segments: [...newList, segment] });
    },

  initTargetsFromSegments:
    () =>
    ({ getState, setState }) => {
      const { segments } = getState();
      const targets = segments.map(({ audienceTarget, keywords, id }) => {
        return { ...audienceTarget, keywords, segmentId: id };
      });
      setState({ targets: sortTargets(targets) });
    },

  addTarget:
    (target) =>
    ({ getState, setState, dispatch }) => {
      dispatch(PRIVATE_ACTIONS.checkUniqueTargets(target));

      const id = uniqueId();
      const targetWithId = { ...target, id };

      const { targets } = getState();
      const newList = [...targets, targetWithId];
      setState({ targets: newList });

      dispatch(PRIVATE_ACTIONS.addCreateChange(id));
    },
  updateTarget:
    (target) =>
    ({ getState, setState, dispatch }) => {
      dispatch(PRIVATE_ACTIONS.checkUniqueTargets(target));

      const { targets } = getState();
      const newList = targets.map((item) => (item.id === target.id ? target : item));
      setState({ targets: newList });
    },
  removeTarget:
    (target) =>
    ({ getState, setState, dispatch }) => {
      const { targets } = getState();
      const newList = targets.filter((item) => item.id !== target.id);
      setState({ targets: newList });

      dispatch(PRIVATE_ACTIONS.addDeleteChange(target.id));
    },

  ensureDefaultTargetPresent:
    () =>
    ({ getState, setState }) => {
      const { targets } = getState();

      // Check if "default" audience target already is present in the list
      const defaultPresent = targets.some(
        ({ targetType }) => targetType === AUDIENCE_TARGET_TYPE.DEFAULT
      );
      if (defaultPresent) {
        return;
      }

      // Add "default" audience segment to the list
      const defaultSegment = {
        targetType: AUDIENCE_TARGET_TYPE.DEFAULT,
      };
      const newList = [...targets, defaultSegment];
      setState({ targets: newList });
    },

  saveTargetsChanges:
    (projectId) =>
    async ({ getState, setState, dispatch }) => {
      const { targets, targetChangesObj } = getState();

      const getTarget = (id) => targets.find((target) => target.id === id);

      setState({ processing: true });
      try {
        // Prepare lists of changes for audience targets
        const changes = getChangesList(targetChangesObj);
        const idsToDelete = getIdsToChange(changes, CHANGE_TYPE.delete);
        const idsToCreate = getIdsToChange(changes, CHANGE_TYPE.create);

        // Existing targets removal
        for (let i = 0; i < idsToDelete.length; i += 1) {
          const id = idsToDelete[i];

          await deleteAudienceTargeting({
            projectId,
            id,
          });
          dispatch(PRIVATE_ACTIONS.removeChange(id));
        }

        // New targets creation
        for (let i = 0; i < idsToCreate.length; i += 1) {
          const id = idsToCreate[i];
          const target = getTarget(id);
          if (!target) {
            return;
          }

          await createAudienceTargeting({
            projectId,
            targetType: target.targetType,
            targetEvaluation: target.targetEvaluation,
            value: target.value,
            keywords: target.keywords,
          });
          dispatch(PRIVATE_ACTIONS.removeChange(id));
        }
      } finally {
        setState({ processing: false });
      }
    },

  hasNonDefaultTargets:
    () =>
    ({ getState }) => {
      const { targets } = getState();
      const nonDefault = withoutDefaultAudienceTarget(targets);
      return nonDefault.length > 0;
    },
};

export const AudienceTargetingStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: NAME,
});
